import React, { useCallback, useState } from "react";
import { Classes, Dialog } from "@blueprintjs/core";
import { RoleData } from "../../models/access-store";
import { AccessModuleData } from "../../models/access-tree";
import styles from "./RoleDetails.module.scss";
import { EmployerName } from "modules/common/components/employer/EmployerName";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { PlanrCheckbox } from "modules/common/components/planr/checkbox/PlanrCheckbox";
import { texts } from "modules/common/texts";
import classnames from "classnames";
import { AccessEditor } from "../editor/AccessEditor";

/** Права должности (страница доступа) или человека (карточка сотрудника и аутсорсера, `personal`) */
export const RoleDetails = ({
  role,
  modules,
  onToggle,
  onReplace,
  onSetAccess,
  personal,
  readonly,
  storageKey,
  admin,
}: RoleProps) => {
  const [adminConfirm, setAdminConfirm] = useState<boolean | null>(null);

  const onAccessToggle = useCallback(
    (functionality: string, state: boolean) => {
      onToggle(role.id, functionality, state);
    },
    [onToggle, role.id]
  );
  const onClickReplace = useCallback(() => onReplace && onReplace(role.id), [onReplace, role.id]);

  // весь модуль разом: новый набор прав должности = прежний ± коды модуля, одним запросом
  const onModuleChange = useCallback(
    (codes: string[], state: boolean) => {
      if (!onSetAccess) return;
      const current = role.access.filter((a) => a.value).map((a) => a.name);
      const next = state ? Array.from(new Set([...current, ...codes])) : current.filter((c) => !codes.includes(c));
      onSetAccess(role.id, next);
    },
    [onSetAccess, role]
  );

  const confirmAdmin = async () => {
    if (admin && adminConfirm !== null && (await admin.onChange(adminConfirm))) {
      setAdminConfirm(null);
    }
  };

  return (
    <div className={`${styles.roleDetails} role-details`}>
      <div className={styles.header}>
        <EmployerName name={role.label} position={role.sublabel} />
        {onReplace && !readonly && (
          <PlanrButton
            type="graybtn"
            size="small"
            icon="general-copy"
            onClick={onClickReplace}
            title="Заменить права этой должности правами другой должности"
          >
            Скопировать из другой должности
          </PlanrButton>
        )}
      </div>

      {admin && (
        <div
          className={classnames(styles.adminBlock, { [styles.adminOn]: admin.value })}
          title={
            admin.canChange
              ? admin.value
                ? "Снять роль администратора — сотрудник снова получит права должности и личные исключения"
                : "Назначить администратором — все права и вход без подтверждения устройства"
              : admin.reason
          }
        >
          <PlanrCheckbox
            checked={admin.value}
            disabled={!admin.canChange}
            onChange={(value) => setAdminConfirm(value)}
          >
            <span className={styles.adminTitle}>Администратор</span>
          </PlanrCheckbox>
          <span className={styles.adminText}>
            Все права приложения, вход без подтверждения устройства, принудительное завершение договоров, все
            задания и финансовые запросы.
            {!admin.canChange && admin.reason && <> {admin.reason}.</>}
          </span>
        </div>
      )}

      {!personal && (
        <div className={styles.hint}>
          Права отдельного сотрудника можно изменить лично: карточка сотрудника → «Права доступа». Там же
          видно, какие права дала должность, а какие изменены лично.
        </div>
      )}

      <AccessEditor
        modules={modules}
        access={role.access}
        onToggle={onAccessToggle}
        personal={personal}
        readonly={readonly}
        storageKey={storageKey}
        onModuleChange={onSetAccess && !personal ? onModuleChange : undefined}
        admin={admin?.value}
      />

      {admin && (
        <Dialog
          isOpen={adminConfirm !== null}
          onClose={() => setAdminConfirm(null)}
          title={adminConfirm ? "Назначить администратором" : "Снять роль администратора"}
          className="figma-dialog"
          backdropClassName="standard"
          canOutsideClickClose={false}
          style={{ width: 520 }}
        >
          <div className={Classes.DIALOG_BODY}>
            {adminConfirm ? (
              <>
                <p>«{role.label}» получит:</p>
                <ul className={styles.adminList}>
                  <li>все права приложения, включая настройки доступа;</li>
                  <li>вход без подтверждения устройства кодом;</li>
                  <li>принудительное завершение договоров;</li>
                  <li>все задания и финансовые запросы.</li>
                </ul>
                <p className={styles.hint}>
                  Личные исключения сотрудника сохранятся, но действовать не будут, пока он администратор.
                </p>
              </>
            ) : (
              <>
                <p>«{role.label}» снова получит права своей должности и личные исключения.</p>
                <p className={styles.hint}>
                  Вход без подтверждения устройства перестанет действовать сразу — при следующем входе
                  понадобится код.
                </p>
              </>
            )}
          </div>
          <div className={Classes.DIALOG_FOOTER}>
            <div className={Classes.DIALOG_FOOTER_ACTIONS}>
              <PlanrButton type="graybtn" size="small" onClick={() => setAdminConfirm(null)}>
                {texts.cancel}
              </PlanrButton>
              <PlanrButton type={adminConfirm ? "danger" : "greenish"} size="small" onClick={confirmAdmin}>
                {adminConfirm ? "Назначить" : "Снять роль"}
              </PlanrButton>
            </div>
          </div>
        </Dialog>
      )}
    </div>
  );
};

interface AdminControl {
  value: boolean;
  canChange: boolean;
  /** Почему нельзя изменить — подсказка у выключенного флажка */
  reason?: string;
  onChange: (value: boolean) => Promise<boolean>;
}

interface RoleProps {
  role: RoleData;
  modules: AccessModuleData[];
  onToggle: (roleId: string, functionality: string, state: boolean) => void;
  onReplace?: (roleId: string) => void;
  /** Заменить набор прав должности целиком — для «Выбрать все / Снять все» в модуле */
  onSetAccess?: (roleId: string, access: string[]) => Promise<boolean>;
  personal?: boolean;
  readonly?: boolean;
  storageKey: string;
  /** Роль администратора (GodMode) — только в карточке сотрудника */
  admin?: AdminControl;
}
